import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { UserRoles, formatDateTime, messages, type UserRole, type UserSummary } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfirmDialog } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/** S72 ユーザー（admin のみ） */
export const SettingsUsersPage = () => {
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [handoverTarget, setHandoverTarget] = useState<{ user: UserSummary; action: 'suspend' | 'delete' } | null>(null);
  const [handoverToUserId, setHandoverToUserId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: qk.users,
    queryFn: () => api.get<SingleResponse<UserSummary[]>>('/users'),
  });

  const updateUser = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      api.patch(`/users/${id}`, payload),
    onSuccess: () => {
      toast.success('変更しました');
      void queryClient.invalidateQueries({ queryKey: qk.users });
      setHandoverTarget(null);
      setHandoverToUserId('');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const deleteUser = useMutation({
    mutationFn: ({ id, handover }: { id: string; handover?: string }) =>
      api.del(`/users/${id}`, handover ? { handoverToUserId: handover } : {}),
    onSuccess: () => {
      toast.success('ユーザーを削除しました');
      void queryClient.invalidateQueries({ queryKey: qk.users });
      setHandoverTarget(null);
      setHandoverToUserId('');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const resend = useMutation({
    mutationFn: (invitationId: string) => api.post(`/users/invitations/${invitationId}/resend`),
    onSuccess: () => toast.success('招待メールを再送しました'),
    onError: (error) => toast.error(errorMessage(error)),
  });

  const activeUsers = data?.data.filter((u) => u.status === 'active') ?? [];

  return (
    <>
      <PageHeader
        back="/settings"
        title={messages.settings.users}
        actions={
          <Button onClick={() => setInviteOpen(true)}>
            <Plus aria-hidden />
            {messages.settings.inviteUser}
          </Button>
        }
      />

      {isLoading ? (
        <SkeletonRows rows={4} />
      ) : (
        <ul className="space-y-2">
          {data?.data.map((user) => (
            <li key={user.id}>
              <Card>
                <CardContent className="flex flex-wrap items-center gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-body font-semibold">{user.name ?? '（未設定）'}</span>
                      <StatusBadge kind="user_status" value={user.status} />
                    </p>
                    <p className="truncate text-note text-foreground-muted">{user.email}</p>
                    <p className="tabular text-note text-foreground-subtle">
                      最終ログイン {formatDateTime(user.lastLoginAt)}
                      {user.assignedCompanyCount != null && user.assignedCompanyCount > 0 && (
                        <> ・ 担当 {user.assignedCompanyCount} 社</>
                      )}
                    </p>
                  </div>

                  <Select
                    value={user.role}
                    onChange={(e) => {
                      if (window.confirm(`権限を「${UserRoles.label(e.target.value)}」に変更しますか。`)) {
                        updateUser.mutate({ id: user.id, payload: { role: e.target.value as UserRole } });
                      }
                    }}
                    aria-label={`${user.name ?? user.email} の権限`}
                    className="h-10 w-36 shrink-0 text-body"
                  >
                    {UserRoles.list.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </Select>

                  <div className="flex shrink-0 gap-2">
                    {user.status === 'invited' && user.invitationExpiresAt && (
                      <Button variant="outline" size="sm" onClick={() => resend.mutate(user.id)}>
                        {messages.settings.resendInvite}
                      </Button>
                    )}
                    {user.status === 'active' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setHandoverTarget({ user, action: 'suspend' })}
                      >
                        {messages.settings.suspendUser}
                      </Button>
                    )}
                    {user.status === 'suspended' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateUser.mutate({ id: user.id, payload: { status: 'active' } })}
                      >
                        {messages.settings.activateUser}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setHandoverTarget({ user, action: 'delete' })}
                    >
                      {messages.common.delete}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />

      <ConfirmDialog
        open={!!handoverTarget}
        onOpenChange={(open) => {
          if (!open) {
            setHandoverTarget(null);
            setHandoverToUserId('');
          }
        }}
        title={handoverTarget?.action === 'delete' ? messages.settings.deleteUser : messages.settings.suspendUser}
        summary={`${handoverTarget?.user.name ?? handoverTarget?.user.email} を${
          handoverTarget?.action === 'delete' ? '削除' : '停止'
        }します。`}
        detail={
          <div className="space-y-3">
            {(handoverTarget?.user.assignedCompanyCount ?? 0) > 0 && (
              <>
                <p className="text-body font-semibold">{messages.settings.handoverRequired}</p>
                <Field label="引き継ぎ先" required>
                  {(props) => (
                    <Select
                      {...props}
                      value={handoverToUserId}
                      onChange={(e) => setHandoverToUserId(e.target.value)}
                    >
                      <option value="">選択してください</option>
                      {activeUsers
                        .filter((u) => u.id !== handoverTarget?.user.id)
                        .map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name ?? u.email}
                          </option>
                        ))}
                    </Select>
                  )}
                </Field>
              </>
            )}
            <p className="text-body text-foreground-muted">
              ログイン中のセッションはすべて無効になります。
            </p>
          </div>
        }
        confirmText={handoverTarget?.action === 'delete' ? messages.common.delete : messages.settings.suspendUser}
        irreversible={handoverTarget?.action === 'delete'}
        onConfirm={() => {
          if (!handoverTarget) return;
          const needsHandover = (handoverTarget.user.assignedCompanyCount ?? 0) > 0;
          if (needsHandover && !handoverToUserId) {
            toast.error('引き継ぎ先を選んでください');
            return;
          }
          if (handoverTarget.action === 'delete') {
            deleteUser.mutate({ id: handoverTarget.user.id, handover: handoverToUserId || undefined });
          } else {
            updateUser.mutate({
              id: handoverTarget.user.id,
              payload: { status: 'suspended', handoverToUserId: handoverToUserId || undefined },
            });
          }
        }}
        isPending={updateUser.isPending || deleteUser.isPending}
      />
    </>
  );
};

const InviteDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('member');

  const mutation = useMutation({
    mutationFn: () => api.post<SingleResponse<{ sent: boolean; message: string }>>('/users/invitations', { email, name: name || undefined, role }),
    onSuccess: (result) => {
      toast.success(result.data.message);
      void queryClient.invalidateQueries({ queryKey: qk.users });
      onOpenChange(false);
      setEmail('');
      setName('');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.settings.inviteUser}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label={messages.auth.email} required>
            {(props) => (
              <Input
                {...props}
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            )}
          </Field>
          <Field label={messages.auth.displayName}>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="権限" required>
            {(props) => (
              <Select {...props} value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
                {UserRoles.list.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}（{item.description}）
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button size="lg" onClick={() => mutation.mutate()} disabled={mutation.isPending || !email.trim()}>
            招待する
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
